import { useMemo, useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { WhatsAppDeEnvio } from '@/modules/ordenes-servicio/WhatsAppDeEnvio'
import { useHerramientasCotizacion } from '@/modules/cotizador/useBorrador'
import { useHerramientasDisponibles } from '@/modules/programacion/useHerramientas'

import {
  enlaceDeCotizacion,
  useDecidirCotizacion,
  useDevolverCotizacion,
  useEnviarCotizacionAlCliente,
  useRegistrarAceptacion,
  useReintentarCotizacion,
} from './useDecidirCotizacion'
import { ResultadoDelEnvio, type Envio } from './ResultadoDelEnvio'
import estilos from './DecidirCotizacion.module.css'

/**
 * La compuerta del precio, donde está el precio.
 *
 * APROBAR NO CIERRA: ENVÍA. Gerencia da el visto bueno interno y la cotización
 * se le manda al cliente, que la acepta o no desde un enlace. Solo con su
 * aceptación nace la orden de trabajo y el trabajo pasa a programación. Antes
 * se programaba con la sola firma de la casa, y quedaba un técnico agendado
 * para algo que el cliente todavía no había aceptado.
 *
 * RECHAZAR PIDE ESCRIBIR; enviar no. No es simetría rota por descuido: la base
 * exige la justificación solo al rechazar, y con razón. Un «sí» se explica
 * solo —el trabajo sigue— mientras que un «no» deja a alguien esperando saber
 * qué corregir, y esa explicación es lo único que convierte el rechazo en
 * información en vez de en un portazo.
 */

type Props = {
  cotizacionId: string
  /** Si ya está con el cliente, lo que queda es esperarlo o repetirle el enlace. */
  yaEnviada?: boolean
  onListo: () => void
  onError: (mensaje: string) => void
}

export function DecidirCotizacion({ cotizacionId, yaEnviada = false, onListo, onError }: Props) {
  const decidir = useDecidirCotizacion()
  const enviar = useEnviarCotizacionAlCliente()
  const registrar = useRegistrarAceptacion()
  const devolver = useDevolverCotizacion()
  const [rechazando, setRechazando] = useState(false)
  // 0302: devolverla al técnico porque le faltó algo.
  const [devolviendo, setDevolviendo] = useState(false)
  const [faltante, setFaltante] = useState('')
  const [motivo, setMotivo] = useState('')
  // Registrar una aceptación que ocurrió por fuera del enlace.
  const [registrandoAcepto, setRegistrandoAcepto] = useState(false)
  const [medio, setMedio] = useState('')
  const [detalle, setDetalle] = useState('')
  const [enviada, setEnviada] = useState<Envio | null>(null)

  const { herramientas: herramientasCotizadas } = useHerramientasCotizacion(cotizacionId)
  const { herramientas: catalogoHerramientas } = useHerramientasDisponibles()

  const listaHerramientas = useMemo(() => {
    return Object.entries(herramientasCotizadas)
      .filter(([, cant]) => cant > 0)
      .map(([id, cant]) => {
        const item = catalogoHerramientas.find((h) => h.id === id)
        return {
          id,
          nombre: item?.nombre ?? 'Herramienta',
          cantidad: cant,
        }
      })
  }, [herramientasCotizadas, catalogoHerramientas])

  async function enviarAlCliente() {
    try {
      const r = await enviar.mutateAsync(cotizacionId)
      setEnviada({ enlace: enlaceDeCotizacion(r.token), salio: r.salio, motivo: r.motivo })
      onListo()
    } catch (fallo) {
      onError(fallo instanceof Error ? fallo.message : 'No se pudo enviar al cliente.')
    }
  }

  async function registrarAcepto() {
    try {
      await registrar.mutateAsync({
        cotizacionId,
        medio,
        notas: detalle.trim() || null,
      })
      setRegistrandoAcepto(false)
      setMedio('')
      setDetalle('')
      onListo()
    } catch (fallo) {
      onError(fallo instanceof Error ? fallo.message : 'No se pudo registrar la aceptación.')
    }
  }

  async function rechazar() {
    try {
      await decidir.mutateAsync({ cotizacionId, aprobar: false, notas: motivo.trim() })
      setRechazando(false)
      setMotivo('')
      onListo()
    } catch (fallo) {
      onError(fallo instanceof Error ? fallo.message : 'No se pudo decidir.')
    }
  }

  if (enviada) return <ResultadoDelEnvio envio={enviada} />

  if (registrandoAcepto) {
    return (
      <div className={estilos.zona}>
        {/* POR DÓNDE ACEPTÓ, y no es un trámite: es lo que separa «el cliente
            aceptó por teléfono» de una aprobación a dedo. Queda escrito en la
            historia del documento con el nombre de quien lo registra. */}
        <label className={estilos.campo}>
          <span>¿Por dónde aceptó el cliente?</span>
          <select value={medio} onChange={(e) => setMedio(e.target.value)} autoFocus>
            <option value="">Escoja…</option>
            <option value="teléfono">Por teléfono</option>
            <option value="WhatsApp">Por WhatsApp</option>
            <option value="en persona">En persona</option>
            <option value="correo">Por correo</option>
          </select>
        </label>

        <label className={estilos.campo}>
          <span>Detalle (opcional)</span>
          <textarea
            rows={2}
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            placeholder="Con quién habló, a qué hora…"
          />
        </label>

        <div className={estilos.acciones}>
          <Boton
            variante="principal"
            onClick={() => void registrarAcepto()}
            trabajando={registrar.isPending}
            disabled={!medio}
          >
            Confirmar que aceptó
          </Boton>
          <Boton
            variante="secundario"
            onClick={() => {
              setRegistrandoAcepto(false)
              setMedio('')
              setDetalle('')
            }}
          >
            Cancelar
          </Boton>
        </div>
      </div>
    )
  }

  if (devolviendo) {
    return (
      <div className={estilos.zona}>
        <label className={estilos.campo}>
          <span>¿Qué le faltó al técnico?</span>
          <textarea
            rows={2}
            value={faltante}
            onChange={(e) => setFaltante(e.target.value)}
            placeholder="Falta el cable del tablero, la mano de obra del cambio de breakers…"
            autoFocus
          />
        </label>

        <div className={estilos.acciones}>
          <Boton
            variante="principal"
            onClick={() =>
              void devolver
                .mutateAsync({ cotizacionId, motivo: faltante.trim() })
                .then(() => {
                  setDevolviendo(false)
                  setFaltante('')
                  onListo()
                })
                .catch((fallo: unknown) =>
                  onError(fallo instanceof Error ? fallo.message : 'No se pudo devolver.'),
                )
            }
            trabajando={devolver.isPending}
            disabled={faltante.trim().length < 5}
          >
            Devolvérsela al técnico
          </Boton>
          <Boton
            variante="secundario"
            onClick={() => {
              setDevolviendo(false)
              setFaltante('')
            }}
          >
            Cancelar
          </Boton>
        </div>
      </div>
    )
  }

  if (rechazando) {
    return (
      <div className={estilos.zona}>
        <label className={estilos.campo}>
          <span>¿Por qué se rechaza?</span>
          <textarea
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="El precio no corresponde al alcance, faltan materiales…"
            autoFocus
          />
        </label>

        <div className={estilos.acciones}>
          <Boton
            variante="peligro"
            onClick={() => void rechazar()}
            trabajando={decidir.isPending}
            // La base pide al menos diez caracteres. Mejor impedirlo aquí que
            // dejar que el servidor devuelva un error por algo que se ve.
            disabled={motivo.trim().length < 10}
          >
            Confirmar el rechazo
          </Boton>
          <Boton
            variante="secundario"
            onClick={() => {
              setRechazando(false)
              setMotivo('')
            }}
          >
            Cancelar
          </Boton>
        </div>
      </div>
    )
  }

  return (
    <>
      {yaEnviada && (
        <Aviso tipo="info" titulo="Con el cliente">
          Ya la tiene y está decidiendo. Cuando acepte, la orden de trabajo nace sola y pasa a
          programación. Si dice que perdió el enlace, se le puede volver a enviar: el anterior queda
          sin efecto.
        </Aviso>
      )}

      {/* A dónde sale, antes de mandarla (0273). */}
      <WhatsAppDeEnvio cotizacionId={cotizacionId} compacto />

      {/* 0318: Herramientas que el técnico estimó al cotizar */}
      {listaHerramientas.length > 0 && (
        <div className={estilos.herramientasAviso}>
          <span className={estilos.herramientasTitulo}>Herramientas requeridas por el técnico:</span>
          <div className={estilos.herramientasChips}>
            {listaHerramientas.map((h) => (
              <span key={h.id} className={estilos.chipHerramienta}>
                {h.nombre} <b>×{h.cantidad}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className={estilos.acciones}>
        <Boton
          variante={yaEnviada ? 'secundario' : 'principal'}
          onClick={() => void enviarAlCliente()}
          trabajando={enviar.isPending}
        >
          {yaEnviada ? 'Volver a enviarle el enlace' : 'Aprobar y enviar al cliente'}
        </Boton>
        {/* Cuando el cliente ya dijo que sí por fuera: el trabajo arranca sin
            esperar un clic que muchas veces no llega. Va junto al de aprobar,
            que es lo mismo por otro camino; «Rechazar», que es lo contrario,
            se aparta a la derecha. Johan: «se ve desordenado». */}
        <Boton variante="secundario" onClick={() => setRegistrandoAcepto(true)}>
          El cliente ya aceptó
        </Boton>
        <span className={estilos.empuje} aria-hidden="true" />
        {/* 0302: antes de mandarla al cliente, se le puede devolver al
            técnico si le faltó algo. */}
        {!yaEnviada && (
          <Boton variante="secundario" onClick={() => setDevolviendo(true)}>
            Devolver al técnico
          </Boton>
        )}
        <Boton variante="peligro" onClick={() => setRechazando(true)}>
          Rechazar
        </Boton>
      </div>
    </>
  )
}

/**
 * Lo que se ve cuando World Office no quiso crear la cotización.
 *
 * SE DICE EL MOTIVO, y ese es el punto. Antes esto se quedaba en «pendiente»
 * con un cartel que prometía un número que nunca iba a llegar, y la razón real
 * —«falta permiso de ejecución sobre NumeracionGralConPrefijo»— moría en una
 * tabla que no mira nadie. El motivo está en la historia del documento, aquí
 * abajo; lo que hace falta es no mentir arriba.
 */
export function CotizacionFallida({
  cotizacionId,
  puedeReintentar,
  onError,
}: {
  cotizacionId: string
  puedeReintentar: boolean
  onError: (mensaje: string) => void
}) {
  const reintentar = useReintentarCotizacion()

  async function volverAPedir() {
    try {
      await reintentar.mutateAsync(cotizacionId)
    } catch (fallo) {
      onError(fallo instanceof Error ? fallo.message : 'No se pudo reintentar.')
    }
  }

  return (
    <>
      <Aviso tipo="alerta" titulo="World Office no la creó">
        La solicitud se mandó y fue rechazada. El motivo exacto está abajo, en la historia del
        documento. Mientras tanto el trabajo quedó sin precio y se puede volver a pedir.
      </Aviso>

      {puedeReintentar && (
        <div className={estilos.acciones}>
          <Boton
            variante="principal"
            onClick={() => void volverAPedir()}
            trabajando={reintentar.isPending}
          >
            Volver a pedirla
          </Boton>
        </div>
      )}
    </>
  )
}
